import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  startTransition,
} from "react";
import PropTypes from "prop-types";
import { Button, Col, Tooltip, OverlayTrigger, Overlay, Popover } from "react-bootstrap";
import GlobalConfig from "../global_config/GlobalConfig";
import NavActionButtons from "./NavActionButtons.jsx";
import NavDropdown from "./NavDropdown.jsx";
import PluginsMenu from "./PluginsMenu.jsx";
import AudioPlugin from "../audio_plugin/AudioPlugin.jsx";
import "./_navBar.scss";
import screenfull from "screenfull";
import ExportModal from "../export/ExportModal";
import SubtitlesModal from "../subtitles/SubtitlesModal";
import ActivityLogModal from "../../activity_log/ActivityLogModal";
import logoIcon from "./../../../../images/logo.svg";
import i18n from "i18next";
import { getUserInfo } from "../../../../common/environment_config";
import { fetchUserProfile } from "../../../../common/server_utils";
import { isClmCourse } from "../../../../common/clm";
import {
  LuLayers,
  LuFolderSearch2,
  LuImage,
  LuBlocks,
  LuEye,
  LuVolume2,
  LuVideo,
  LuMusic,
  LuUser,
  LuLayoutTemplate,
  LuFileText,
  LuSparkles,
  LuWandSparkles,
  LuRefreshCw,
  LuCircleHelp,
  LuBotMessageSquare,
  LuInfo,
} from "react-icons/lu";
// import AiSuggestionsButton from "./AiSuggestionsButton.jsx";
// import WorkflowButton from "./WorkflowButton.jsx";
import { BiUserVoice } from "react-icons/bi";
import BetaTag from "../../common/BetaTag.jsx";
import AiTag from "../../common/AiTag.jsx";
import { useBuildLocked } from "../../ai-chat/buildMode";
import { BETA_ORIGIN, switchToBeta } from "./betaSwitch";

/**
 * Show the "Animator (Beta)" entry in the AI action row (next to the question
 * generator). Hidden on purpose while the feature is still beta — set to true
 * to bring it back. Nothing else was removed: AnimatorPanel and its
 * "aidea:openAnimatorPanel" listener stay in place, so flipping this is the
 * only change needed.
 */
const SHOW_ANIMATOR_ACTION = false;

/**
 * Upper navigation bar component
 */
const EditorNavBar = (props) => {
  const [showImportFile, setShowImportFile] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [userCredits, setUserCredits] = useState(null);
  const [orgName, setOrgName] = useState(null);
  // Which limit the shown number comes from — see creditsInfoBody below.
  const [creditScope, setCreditScope] = useState("organization");
  const [creditTeam, setCreditTeam] = useState(null);
  const [creditsRefreshing, setCreditsRefreshing] = useState(false);
  const buildLocked = useBuildLocked();
  const autosaveTargetRef = useRef(null);
  const isClm = isClmCourse(props.globalConfig);
  const userInfo = getUserInfo();
  const userInitials = userInfo.name
    ? userInfo.name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : (userInfo.email || "?")[0].toUpperCase();

  const refreshCredits = useCallback(() => {
    setCreditsRefreshing(true);
    fetchUserProfile(window.idealStudioTrainingId).then((user) => {
      // availableCredits = what THIS user can spend: the org balance narrowed
      // by their team budget (own share / team pool / team cap). Older
      // middlewares only send monthlyMinutesLimit, the org balance.
      const credits = user?.availableCredits ?? user?.monthlyMinutesLimit;
      if (credits != null) setUserCredits(Number(credits));
      if (user) {
        setCreditScope(user.creditScope || "organization");
        setCreditTeam(user.team || null);
      }
      if (user?.organizationName) setOrgName(user.organizationName);
      setCreditsRefreshing(false);
    });
  }, []);

  // The number is the org balance unless the user's team budget is tighter;
  // then it is their own share (`member`) or what the team has left
  // (`pool` / `team`). Label and explanation follow the same split.
  const isTeamScope = creditScope !== "organization" && !!creditTeam;
  const creditLabel = isTeamScope ? creditTeam.name : orgName;
  const teamResetDate =
    isTeamScope && creditTeam.resetsAt
      ? new Date(creditTeam.resetsAt).toLocaleDateString(i18n.language || "tr")
      : "";
  let creditsInfoBody;
  if (isTeamScope && creditScope === "member") {
    creditsInfoBody = i18n.t("navbar.creditsInfo.bodyMember", {
      team: creditTeam.name,
      date: teamResetDate,
      defaultValue:
        "Bu, {{team}} ekibinde size bu ay ayrılan kredinin kalanıdır. Limit {{date}} tarihinde yenilenir.",
    });
  } else if (isTeamScope) {
    creditsInfoBody = i18n.t("navbar.creditsInfo.bodyTeam", {
      team: creditTeam.name,
      date: teamResetDate,
      defaultValue:
        "Bu, {{team}} ekibinin bu ay kullanabileceği kalan kredidir; ekipteki diğer kişilerle ortak kullanılır. Limit {{date}} tarihinde yenilenir.",
    });
  } else {
    creditsInfoBody = i18n.t("navbar.creditsInfo.body", {
      defaultValue:
        "Bu, bu eğitimin kredisinin düşüldüğü kuruluşun kalan toplam kredisidir. Yalnızca bu eğitim için harcananı değil, tüm hesabın kullanılabilir bakiyesini gösterir.",
    });
  }

  useEffect(() => {
    refreshCredits();
    // Credits poll — once a minute is enough for a balance display.
    const interval = setInterval(refreshCredits, 60000);
    return () => clearInterval(interval);
  }, []);
  // Separate state for the standalone "Video Export" entry point — video
  // is a fundamentally different operation (long-running queue, manual
  // merge, scene-strip UI) from SCORM/PDF/URL share so it gets its own
  // menu item that opens the modal in videoOnly mode.
  const [showVideoExport, setShowVideoExport] = useState(false);
  // Same standalone-entry-point pattern as Video Export above — Subtitles
  // is its own middleware-backed feature (view/generate/delete per-language
  // scene subtitles), unrelated to SCORM/PDF/URL export, so it gets its own
  // menu item + modal rather than being folded into ExportModal's format picker.
  const [showSubtitles, setShowSubtitles] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [creditsInfoOpen, setCreditsInfoOpen] = useState(false);
  const creditsInfoRef = useRef(null);
  const [isFullScreenOn] = useState(screenfull.isFullscreen);
  const [showSoundBtn, setShowSoundBtn] = useState(false);
  // Overflow-driven collapse — no fixed breakpoints. Stages: 0 full,
  // 1 smaller AI labels, 2 AI labels hidden, 3 all icon labels hidden,
  // 4 single AI button + popover. Escalates only while #iconBar actually
  // overflows (it is overflow:hidden, so overflow would silently clip the
  // Save/Preview controls).
  const [navStage, setNavStage] = useState(0);
  const compactAiPill = navStage >= 4;
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const aiMenuTargetRef = useRef(null);
  // Expansion works by probing: step down a stage, and if it overflows the
  // layout-effect recheck reverts before paint. A probe that failed is not
  // retried until the layout signature (bar/content widths) changes.
  const probeFailSigRef = useRef({});
  const pendingProbeRef = useRef(null);

  const checkNavOverflow = useCallback(() => {
    const el = document.getElementById("iconBar");
    if (!el) return;
    // The pill is min-width:0 + overflow:hidden, so its content clips before
    // the bar itself overflows — check both.
    const pill = el.querySelector(".nav-ai-pill");
    const overflowing =
      el.scrollWidth > el.clientWidth + 1 || (pill && pill.scrollWidth > pill.clientWidth + 1);
    const leftGroup = el.querySelector(".navLeftGroup");
    const right = el.querySelector(".headerTopRight");
    const sig = [
      Math.round(el.clientWidth / 8),
      right ? Math.round(right.offsetWidth / 4) : 0,
      ...(leftGroup ? Array.from(leftGroup.children, (c) => Math.round(c.offsetWidth / 4)) : []),
    ].join(",");
    setNavStage((prev) => {
      if (overflowing) {
        if (pendingProbeRef.current) {
          probeFailSigRef.current[pendingProbeRef.current.stage] = pendingProbeRef.current.sig;
          pendingProbeRef.current = null;
        }
        return prev < 4 ? prev + 1 : prev;
      }
      if (prev > 0 && probeFailSigRef.current[prev - 1] !== sig) {
        pendingProbeRef.current = { stage: prev - 1, sig };
        return prev - 1;
      }
      pendingProbeRef.current = null;
      return prev;
    });
  }, []);

  useEffect(() => {
    const el = document.getElementById("iconBar");
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(checkNavOverflow);
    ro.observe(el);
    // Content width also changes without a container resize (credits load, i18n).
    el.querySelectorAll(".headerTop > div").forEach((child) => ro.observe(child));
    const pill = el.querySelector(".nav-ai-pill");
    if (pill) ro.observe(pill);
    document.fonts?.ready?.then(checkNavOverflow).catch(() => {});
    checkNavOverflow();
    return () => ro.disconnect();
  }, [checkNavOverflow]);

  // Re-evaluate synchronously after a stage change so a failed expansion
  // probe is reverted before the browser paints.
  useLayoutEffect(() => {
    checkNavOverflow();
    if (navStage < 4) setAiMenuOpen(false);
  }, [navStage, checkNavOverflow]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === "e") {
        e.preventDefault();
        setShowSoundBtn((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const toggleExport = useCallback((forceClose) => {
    setShowExport((prev) => (forceClose ? false : !prev));
  }, []);

  const toggleVideoExport = useCallback((forceClose) => {
    setShowVideoExport((prev) => (forceClose ? false : !prev));
  }, []);

  const toggleSubtitles = useCallback((forceClose) => {
    setShowSubtitles((prev) => (forceClose ? false : !prev));
  }, []);

  const toggleActivityLog = useCallback((forceClose) => {
    setShowActivityLog((prev) => (forceClose ? false : !prev));
  }, []);

  const toggleImportFile = useCallback(() => {
    // Previous class component declared showImportFile in state but did not
    // expose any behavior; we keep a local toggle here in case it is used
    // in the future while preserving the external API shape.
    setShowImportFile((prev) => !prev);
  }, []);

  const renderTooltip = (text) => <Tooltip id={`tooltip-${text}`}>{i18n.t(text)}</Tooltip>;

  // Tooltip only while the button is icon-only (its label collapsed away),
  // redundant otherwise. minStage = the stage at which the label disappears.
  const withIconOnlyTooltip = (label, node, minStage = 3) =>
    navStage >= minStage ? (
      <OverlayTrigger
        placement="bottom"
        overlay={<Tooltip id={`tooltip-${label}`}>{label}</Tooltip>}
      >
        {node}
      </OverlayTrigger>
    ) : (
      node
    );

  // Rendered as the wide pill row or as items of the collapsed popover menu.
  const aiActions = [
    {
      key: "addImage",
      icon: <LuImage size={20} />,
      label: i18n.t("AddImage"),
      tooltip: "AddImage",
      active: !!props.showAddImageModal,
      onClick: props.onAddImageModalToggle,
    },
    {
      key: "addVideo",
      icon: <LuVideo size={20} />,
      label: i18n.t("AddVideo"),
      tooltip: "AddVideo",
      active: !!props.showAddVideoModal,
      onClick: props.onAddVideoModalToggle,
    },
    {
      key: "addAvatar",
      icon: <LuUser size={20} />,
      label: i18n.t("AddAvatar"),
      tooltip: "AddAvatar",
      active: !!props.showAddSoundModalNew,
      onClick: props.onAddAvatarModalToggle,
    },
    {
      key: "addSound",
      icon: <BiUserVoice size={20} />,
      label: i18n.t("SoundSound"),
      tooltip: "SoundSound",
      active: !!props.showAddSoundModalNew,
      onClick: props.onAddSoundModalNewToggle,
    },
    ...(showSoundBtn
      ? [
          {
            key: "soundLayers",
            icon: <BiUserVoice size={20} />,
            label: i18n.t("SoundSound"),
            tooltip: "SoundSound",
            active: props.category === "audio",
            onClick: () => {
              props.onLeftPanelChange && props.onLeftPanelChange("layers");
              props.onSelectTopLayer && props.onSelectTopLayer();
            },
          },
        ]
      : []),
    {
      key: "infographic",
      icon: <LuLayoutTemplate size={20} />,
      label: i18n.t("InfographicScene"),
      tooltip: "InfographicScene",
      active: !!props.showInfographicSceneModal,
      onClick: props.onInfographicSceneModalToggle,
    },
    {
      key: "quiz",
      icon: <span style={{ fontSize: "18px", fontWeight: "bold", lineHeight: 1 }}>?</span>,
      label: i18n.t("QuestionGenerator.Title"),
      tooltip: "QuestionGenerator.Title",
      active: !!props.showQuestionGeneratorModal,
      onClick: props.onQuestionGeneratorModalToggle,
    },
    // Animator (Beta) — hidden for now, NOT removed. Flip SHOW_ANIMATOR_ACTION
    // back to true to restore it; the panel, its event listener and the
    // AnimatorPanel component are all still wired up and untouched.
    ...(SHOW_ANIMATOR_ACTION
      ? [
          // Opens the read-only Animator panel; the AI badge opens the assistant.
          {
            key: "animator",
            icon: <LuSparkles size={20} className="merlin-icon-animated" />,
            label: i18n.t("Animator", { defaultValue: "Animator" }),
            tooltip: "Animator",
            beta: true,
            onClick: () => window.dispatchEvent(new CustomEvent("aidea:openAnimatorPanel")),
          },
        ]
      : []),
    // CLM courses: Infographic Scene and Question Generator can both emit
    // plugins outside the allowlist (multi-plugin infographic layouts;
    // TrueFalse/DragDrop/MultipleChoice) — hidden entirely (spec §3.3 item
    // 1).
  ].filter((action) => !isClm || (action.key !== "infographic" && action.key !== "quiz"));

  return (
    <Col id="iconBar" className={navStage > 0 ? `nav-stage-${navStage}` : undefined}>
      {/* <div className="grad1" />*/}
      <div className="identity" onClick={props.openExitModal}>
        <img src={logoIcon} width="100%" height="40" alt="Logo" />
        <div className="navDivider" />
      </div>
      <div className="headerTop">
        <div className="navLeftGroup" style={{ flex: 1, minWidth: 0 }}>
          {withIconOnlyTooltip(
            i18n.t("Scenes"),
            <button
              type="button"
              className="navIconButton"
              onClick={() => props.onLeftPanelChange && props.onLeftPanelChange("layers")}
            >
              <LuLayers
                size={22}
                color={props.activeLeftPanel === "layers" ? "#B766FA" : "#3D4B5C"}
              />
              <span
                className="navIconLabel"
                style={{ color: props.activeLeftPanel === "layers" ? "#B766FA" : "#3D4B5C" }}
              >
                {i18n.t("Scenes")}
              </span>
            </button>
          )}
          {withIconOnlyTooltip(
            i18n.t("components"),
            <button
              type="button"
              className="navIconButton"
              data-testid="nav-etkilesim-chip"
              style={{ position: "relative" }}
              onClick={() => props.onLeftPanelChange && props.onLeftPanelChange("elementsLibrary")}
            >
              <LuBlocks
                size={22}
                color={props.activeLeftPanel === "elementsLibrary" ? "#B766FA" : "#3D4B5C"}
              />
              <span
                className="navIconLabel"
                style={{
                  color: props.activeLeftPanel === "elementsLibrary" ? "#B766FA" : "#3D4B5C",
                }}
              >
                {i18n.t("components")}
              </span>
              <BetaTag />
            </button>
          )}
          {withIconOnlyTooltip(
            i18n.t("Library"),
            <button
              type="button"
              className="navIconButton"
              onClick={() => props.onLeftPanelChange && props.onLeftPanelChange("library")}
            >
              <LuFolderSearch2
                size={22}
                color={props.activeLeftPanel === "library" ? "#B766FA" : "#3D4B5C"}
              />
              <span
                className="navIconLabel"
                style={{ color: props.activeLeftPanel === "library" ? "#B766FA" : "#3D4B5C" }}
              >
                {i18n.t("Library")}
              </span>
            </button>
          )}
          {withIconOnlyTooltip(
            i18n.t("Audio"),
            <button
              type="button"
              className="navIconButton"
              onClick={() => props.onLeftPanelChange && props.onLeftPanelChange("audio")}
            >
              <LuFileText
                size={22}
                color={props.activeLeftPanel === "audio" ? "#B766FA" : "#3D4B5C"}
              />
              <span
                className="navIconLabel"
                style={{ color: props.activeLeftPanel === "audio" ? "#B766FA" : "#3D4B5C" }}
              >
                {i18n.t("Audio")}
              </span>
            </button>
          )}
          {/* WorkflowButton hidden */}
          {/* <WorkflowButton isActive={props.activeLeftPanel === "workflow"} /> */}
          <div className="navDivider" />
          <div className="nav-ai-pill">
            <OverlayTrigger placement="bottom" overlay={renderTooltip("AI Agent")}>
              <button
                type="button"
                className="nav-ai-pill__badge"
                onClick={() => window.dispatchEvent(new CustomEvent("aidea:openAssistant"))}
                aria-label={i18n.t("AI Agent", { defaultValue: "AI Agent" })}
              >
                <LuBotMessageSquare size={20} />
              </button>
            </OverlayTrigger>

            {!compactAiPill &&
              aiActions.map((action) => (
                <React.Fragment key={action.key}>
                  {withIconOnlyTooltip(
                    action.label,
                    <button
                      type="button"
                      className={`nav-ai-pill__btn${action.active ? " nav-ai-pill__btn--active" : ""}`}
                      onClick={action.onClick}
                    >
                      {action.icon}
                      <span className="nav-ai-pill__btn-label">{action.label}</span>
                      <AiTag />
                      {action.beta && <span className="nav-ai-pill__betaBadge">Beta</span>}
                    </button>,
                    2
                  )}
                </React.Fragment>
              ))}

            {compactAiPill && (
              <>
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id="tooltip-aiToolsMenu">
                      {i18n.t("aiToolsMenu", { defaultValue: "AI Tools" })}
                    </Tooltip>
                  }
                >
                  <button
                    type="button"
                    ref={aiMenuTargetRef}
                    className={`nav-ai-pill__btn${aiMenuOpen ? " nav-ai-pill__btn--active" : ""}`}
                    onClick={() => setAiMenuOpen((prev) => !prev)}
                    aria-expanded={aiMenuOpen}
                    aria-label={i18n.t("aiToolsMenu", { defaultValue: "AI Tools" })}
                  >
                    <LuWandSparkles size={20} />
                    <span className="nav-ai-pill__btn-label">
                      {i18n.t("aiTools", { defaultValue: "AI" })}
                    </span>
                    <AiTag />
                  </button>
                </OverlayTrigger>
                <Overlay
                  rootClose
                  show={aiMenuOpen}
                  placement="bottom"
                  target={() => aiMenuTargetRef.current}
                  onHide={() => setAiMenuOpen(false)}
                >
                  <Popover id="navAiMenuPopover">
                    <Popover.Body>
                      {aiActions.map((action) => (
                        <button
                          key={action.key}
                          type="button"
                          className={`nav-ai-menu__item${action.active ? " nav-ai-menu__item--active" : ""}`}
                          onClick={() => {
                            setAiMenuOpen(false);
                            action.onClick && action.onClick();
                          }}
                        >
                          <span className="nav-ai-menu__itemIcon">{action.icon}</span>
                          <span className="nav-ai-menu__itemLabel">{action.label}</span>
                          {action.beta && <span className="nav-ai-pill__betaBadge">Beta</span>}
                        </button>
                      ))}
                    </Popover.Body>
                  </Popover>
                </Overlay>
              </>
            )}

            {/* <AiSuggestionsButton /> */}
          </div>
          {/* Already on beta → nothing to switch to. */}
          {window.location.origin !== BETA_ORIGIN && (
            <button type="button" className="nav-beta-btn" onClick={switchToBeta}>
              <LuSparkles size={18} />
              <span className="nav-beta-btn__label">{"Beta'ya Geç"}</span>
            </button>
          )}
        </div>
        <div className="headerTopRight">
          <div className="userControls">
            <div className="userDivider" />
            <div className="userControlsStack">
              <div className="userControlsButtons">
                {/* Help — opens the in-editor Help Hub (feature guides: video + text
                    + index, with global search). Fires a window event HelpHub listens for. */}
                {withIconOnlyTooltip(
                  i18n.t("help.nav", { defaultValue: "Help" }),
                  <Button
                    variant="outline"
                    onClick={() => window.dispatchEvent(new CustomEvent("aidea:openHelpHub"))}
                  >
                    {" "}
                    <LuCircleHelp />{" "}
                    <span className="userBtnLabel">
                      {i18n.t("help.nav", { defaultValue: "Help" })}
                    </span>
                  </Button>
                )}
                {withIconOnlyTooltip(
                  i18n.t("preview") || "Preview",
                  <Button variant="outline" onClick={props.visor}>
                    {" "}
                    <LuEye /> <span className="userBtnLabel">{i18n.t("preview") || "Preview"}</span>
                  </Button>
                )}
                {buildLocked ? (
                  // Live Motion Build: saving unlocks when the build finishes. The span
                  // carries the tooltip — a disabled button fires no mouse events.
                  <OverlayTrigger
                    placement="bottom"
                    overlay={renderTooltip("aideaChat.build.saveLocked")}
                  >
                    <span>
                      <Button disabled style={{ pointerEvents: "none" }}>
                        {i18n.t("save") || "Save"}
                      </Button>
                    </span>
                  </OverlayTrigger>
                ) : (
                  <Button onClick={props.save} aria-label="Save">
                    {i18n.t("save") || "Save"}
                  </Button>
                )}
              </div>
              <div className="autosaveRow" ref={autosaveTargetRef}>
                <label
                  className={`autosaveToggle ${
                    props.hasUnsavedChanges ? "is-unsaved" : "is-saved"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!!props.autosaveEnabled}
                    onChange={(e) => props.onAutosaveChange(e.target.checked)}
                  />
                  <span>
                    {i18n.t("navbar.autosave.label", {
                      defaultValue: "Autosave (every 2 min)",
                    })}
                  </span>
                </label>
                <div className="userInfoCard">
                  <span className="userInfoName">{userInfo.name || userInfo.email}</span>
                  <div className="userInfoDivider" />
                  <OverlayTrigger
                    placement="bottom"
                    overlay={
                      userCredits != null ? (
                        <Tooltip id="user-credits-tooltip">
                          {userCredits.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}{" "}
                          {i18n.t("navbar.credits")}
                          {creditLabel ? ` · ${creditLabel}` : ""}
                        </Tooltip>
                      ) : (
                        <span />
                      )
                    }
                  >
                    <span className="userInfoCredits">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="#d97706">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                      </svg>
                      {userCredits != null
                        ? userCredits >= 1000
                          ? (userCredits / 1000).toFixed(1) + "K"
                          : // A team share can be single digits: 0.53 left must
                            // not read as "0". Large balances stay whole.
                            userCredits.toLocaleString("tr-TR", {
                              maximumFractionDigits: userCredits < 100 ? 2 : 0,
                            })
                        : "..."}{" "}
                      {i18n.t("navbar.credits")}
                      <button
                        className={`creditsRefreshBtn${creditsRefreshing ? " spinning" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          refreshCredits();
                        }}
                        title="Yenile"
                      >
                        <LuRefreshCw size={9} />
                      </button>
                      {/* Explains WHAT the number is. The surrounding hover
                          tooltip only restates the figure, which does not tell
                          a first-time author whether it is this course's cost,
                          their personal quota, or the organization's pool.
                          Click, not hover: the parent already owns hover, and
                          two overlays on one element fight each other. */}
                      <button
                        ref={creditsInfoRef}
                        type="button"
                        className="creditsInfoBtn"
                        aria-label={i18n.t("navbar.creditsInfo.title", {
                          defaultValue: "Kalan kredi",
                        })}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCreditsInfoOpen((prev) => !prev);
                        }}
                      >
                        <LuInfo size={10} />
                      </button>
                    </span>
                  </OverlayTrigger>
                  <Overlay
                    rootClose
                    show={creditsInfoOpen}
                    placement="bottom"
                    target={() => creditsInfoRef.current}
                    onHide={() => setCreditsInfoOpen(false)}
                  >
                    <Popover id="creditsInfoPopover">
                      <Popover.Body>
                        <div className="creditsInfo__title">
                          {i18n.t("navbar.creditsInfo.title", {
                            defaultValue: "Kalan kredi",
                          })}
                        </div>
                        <p className="creditsInfo__text">{creditsInfoBody}</p>
                        {userCredits != null && (
                          <div className="creditsInfo__value">
                            {userCredits.toLocaleString(i18n.language || "tr", {
                              maximumFractionDigits: 2,
                            })}{" "}
                            {i18n.t("navbar.credits")}
                            {creditLabel ? ` · ${creditLabel}` : ""}
                          </div>
                        )}
                      </Popover.Body>
                    </Popover>
                  </Overlay>
                </div>
                {props.autosavePulseVisible && (
                  <button
                    type="button"
                    className="unsavedPulseBtn"
                    onClick={props.save}
                    title={i18n.t("navbar.autosave.pulseTooltip", {
                      defaultValue: "Unsaved changes — click to save",
                    })}
                  >
                    <span className="unsavedPulseBtn__dot merlin-icon-animated" />
                  </button>
                )}
                <Overlay
                  rootClose
                  show={!!props.autosavePromptVisible}
                  placement="bottom"
                  target={() => autosaveTargetRef.current}
                  // A click anywhere else closes the prompt. That click may be the
                  // one that starts playback: the dismissal must not re-render
                  // the editor ahead of it.
                  onHide={() => startTransition(() => props.onAutosaveChange(false))}
                >
                  <Popover id="autosavePromptPopover">
                    <Popover.Body>
                      <div className="autosavePromptText">
                        {i18n.t("navbar.autosave.prompt", {
                          defaultValue:
                            "Turn on autosave for this session? Saves automatically every 2 minutes when there are unsaved changes.",
                        })}
                      </div>
                      <div className="autosavePromptActions">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => props.onAutosaveChange(false)}
                        >
                          {i18n.t("navbar.autosave.notNow", { defaultValue: "Not now" })}
                        </Button>
                        <Button size="sm" onClick={() => props.onAutosaveChange(true)}>
                          {i18n.t("navbar.autosave.enable", { defaultValue: "Enable" })}
                        </Button>
                      </div>
                    </Popover.Body>
                  </Popover>
                </Overlay>
              </div>
            </div>
          </div>
          <NavDropdown
            navItemSelected={props.navItemSelected}
            opens={props.opens}
            saveAs={props.saveAs}
            openRatioReproduce={props.openRatioReproduce}
            openNewTraining={props.openNewTraining}
            openExitModal={props.openExitModal}
            openTour={props.openTour}
            serverModalOpen={props.serverModalOpen}
            toggleGlobalConfig={props.toggleGlobalConfig}
            toggleImportFile={toggleImportFile}
            toggleExport={toggleExport}
            toggleVideoExport={toggleVideoExport}
            toggleSubtitles={toggleSubtitles}
            toggleActivityLog={toggleActivityLog}
            toggleFileUpload={props.toggleFileUpload}
            onLeftPanelChange={props.onLeftPanelChange}
            isBusy={props.isBusy}
            undoDisabled={props.undoDisabled}
            exportToLMS={props.exportToLMS}
            isLmsConnect={props.isLmsConnect}
            lmsExporting={props.lmsExporting}
          />
        </div>
      </div>
      <GlobalConfig
        show={props.showGlobalConfig}
        mode={props.globalConfigMode || "edit"}
        forced={props.globalConfigForced || false}
        globalConfig={props.globalConfig}
        onAddImageModalToggle={props.onAddImageModalToggle}
        changeGlobalConfig={props.changeGlobalConfig}
        batchUpdateMandatoryDefaults={props.batchUpdateMandatoryDefaults}
        batchUpdateSceneDuration={props.batchUpdateSceneDuration}
        batchSetSceneFont={props.batchSetSceneFont}
        onCreateNewTraining={props.onCreateNewTraining}
        onRequestSaveAs={props.onRequestSaveAs}
        uploadFunction={props.uploadFunction}
        close={props.toggleGlobalConfig}
        navItems={props.navItems}
        exercises={props.exercises}
        pluginToolbars={props.pluginToolbars}
        onConfigScore={props.onConfigScore}
      />
      <ExportModal
        aspectRatio={props.globalConfig.canvasRatio}
        show={showExport}
        export={props.export}
        baseUrl={props.baseUrl}
        scorm={props.scorm}
        close={toggleExport}
        trainingId={props.trainingId}
        trainingName={props.globalConfig.title}
        globalConfig={props.globalConfig}
        changeGlobalConfig={props.changeGlobalConfig}
        exportClm={props.exportClm}
        validateClm={props.validateClm}
        getExportScenes={props.getExportScenes}
        onSave={props.save}
      />
      {/* Standalone Video Export — same modal in videoOnly mode so the
          video flow doesn't get confused with the share/SCORM export.
          NOTE: don't pass `isDirty` derived from `undoDisabled` — that
          flag tracks whether there's anything to undo, not whether the
          doc has unsaved changes. After a save the undo history stays
          intact and `undoDisabled` is still false, so the gate would
          loop forever on "Önce kaydetmeniz gerekiyor". We let the gate
          rely solely on the server-side exportedState check. */}
      <ExportModal
        aspectRatio={props.globalConfig.canvasRatio}
        show={showVideoExport}
        videoOnly
        export={props.export}
        baseUrl={props.baseUrl}
        scorm={props.scorm}
        close={toggleVideoExport}
        trainingId={props.trainingId}
        trainingName={props.globalConfig.title}
        onSave={props.save}
      />
      <SubtitlesModal
        open={showSubtitles}
        onClose={toggleSubtitles}
        trainingId={props.trainingId}
        trainingName={props.globalConfig.title}
      />
      <ActivityLogModal
        show={showActivityLog}
        onClose={() => toggleActivityLog(true)}
        trainingId={props.trainingId || window.idealStudioTrainingId}
      />
      {/* <PluginsMenu
          category={props.category}
          toggleFileUpload={props.toggleFileUpload}
          hideTab={props.hideTab}
          setcat={props.setcat}
        /> 
          <PluginsMenu
            category={props.category}
            toggleFileUpload={props.toggleFileUpload}
            hideTab={props.hideTab}
            setcat={props.setcat}
          /> 
        <NavActionButtons
          boxSelected={props.boxSelected}
          changeGlobalConfig={props.changeGlobalConfig}
          globalConfig={props.globalConfig}
          navItemSelected={props.navItemSelected}
          navItems={props.navItems}
          redo={props.redo}
          redoDisabled={props.redoDisabled}
          publishing={props.publishing}
          save={props.save}
          saveAs={props.saveAs}
          serverModalOpen={props.serverModalOpen}
          undo={props.undo}
          undoDisabled={props.undoDisabled}
          onBoxSelected={props.onBoxSelected}
          onAIStudioToggle={props.onAIStudioToggle}
          visor={props.visor}
        />
        
       
        
       
        */}
    </Col>
  );
};

EditorNavBar.propTypes = {
  /**
   *  Shows/hides the plugin tab
   */
  hideTab: PropTypes.oneOf(["show", "hide"]).isRequired,
  /**
   * Object containing the global configuration of the document
   */
  globalConfig: PropTypes.object.isRequired,
  /**
   * Modifies the global configuration of the document
   */
  changeGlobalConfig: PropTypes.func.isRequired,
  /**
   * Shows the global configuration of the document
   */
  showGlobalConfig: PropTypes.bool.isRequired,
  /**
   * Toggles the global configuration of the document
   */
  toggleGlobalConfig: PropTypes.func.isRequired,
  /**
   * Allows the use of the undo funtion
   */
  undoDisabled: PropTypes.bool,
  /**
   * Allows the use of the redo function
   */
  redoDisabled: PropTypes.bool,
  /**
   * Current selected view (by ID)
   */
  navItemSelected: PropTypes.any.isRequired,
  /**
   * Current selected box
   */
  boxSelected: PropTypes.any.isRequired,
  /**
   * Undoes the last change
   */
  undo: PropTypes.func.isRequired,
  /**
   * Redoes the last change
   */
  redo: PropTypes.func.isRequired,
  /**
   * Activates preview mode
   */
  visor: PropTypes.func.isRequired,
  /**
   * Exports the document to HTML
   */
  export: PropTypes.func.isRequired,
  /**
   * Exports the document to SCORM
   */
  scorm: PropTypes.func.isRequired,
  /**
   * CLM export: builds the static zip / runs the static validation
   */
  exportClm: PropTypes.func,
  validateClm: PropTypes.func,
  getExportScenes: PropTypes.func,
  /**
   * Saves the changes in the server
   */
  save: PropTypes.func.isRequired,
  /**
   * Whether the opt-in, session-scoped autosave checkbox is checked
   */
  autosaveEnabled: PropTypes.bool,
  /**
   * Whether the unsaved-changes pulse next to the Save button should be shown right now
   */
  autosavePulseVisible: PropTypes.bool,
  /**
   * Whether the doc has unsaved changes — colors the Save button dot
   * yellow (unsaved) vs green (saved)
   */
  hasUnsavedChanges: PropTypes.bool,
  /**
   * Whether the non-blocking "turn on autosave?" ask-on-load prompt should be shown
   */
  autosavePromptVisible: PropTypes.bool,
  /**
   * Single handler for both the checkbox and the ask-on-load prompt's Enable/Not now
   */
  onAutosaveChange: PropTypes.func.isRequired,
  /**
   * Selected plugin category
   */
  category: PropTypes.string.isRequired,
  /**
   * Load server changes
   */
  opens: PropTypes.func.isRequired,
  /**
   * Opens a modal indicating the server operation status
   */
  serverModalOpen: PropTypes.func.isRequired,
  /**
   * Callback for opening the file upload modal
   */
  toggleFileUpload: PropTypes.func.isRequired,
  /**
   * Last files uploaded to server or searched in modal
   */
  fileModalResult: PropTypes.object,
  /**
   * Changes the category of plugins selected
   */
  setcat: PropTypes.func.isRequired,

  /**
   * Object that contains all created views (identified by its *id*)
   */
  navItems: PropTypes.object.isRequired,
  /**
   * Shows exit modal
   */
  openExitModal: PropTypes.func.isRequired,
  /**
   * Opens Tour Modal
   */
  openTour: PropTypes.func.isRequired,
  /**
   * Publish the document
   */
  publishing: PropTypes.func.isRequired,
  /**
   * Exports the document to LMS
   */
  exportToLMS: PropTypes.func.isRequired,
  /**
   * Whether LMS connection is available
   */
  isLmsConnect: PropTypes.bool,
  /**
   * Whether LMS export is in progress
   */
  lmsExporting: PropTypes.bool,
  /**
   * Indicates if there is a current server operation
   */
  isBusy: PropTypes.any,
  /**
   *  Function for uploading a file to the server
   */
  uploadFunction: PropTypes.func.isRequired,
  /**
   * Function for selecting a box
   */
  onBoxSelected: PropTypes.func.isRequired,
  /**
   * Function to toggle AI Studio toolbar
   */
  onAIStudioToggle: PropTypes.func,
  /**
   * Changes left side panel in editor (layers, add, interactions, search)
   */
  onLeftPanelChange: PropTypes.func,
  /**
   * Currently active left panel key
   */
  activeLeftPanel: PropTypes.string,

  /**
   * Function to select the top layer
   */
  onSelectTopLayer: PropTypes.func,
  /**
   * Toggle Add Image modal visibility
   */
  onAddImageModalToggle: PropTypes.func,
  /**
   * Whether Add Image modal is open
   */
  showAddImageModal: PropTypes.bool,
  /**
   * Toggle Add Video modal visibility
   */
  onAddVideoModalToggle: PropTypes.func,
  /**
   * Whether Add Video modal is open
   */
  showAddVideoModal: PropTypes.bool,
  /**
   * Toggle Add Sound modal visibility
   */
  onAddSoundModalToggle: PropTypes.func,
  /**
   * Whether Add Sound modal is open
   */
  showAddSoundModal: PropTypes.bool,
  /**
   * Toggle new Add Sound modal visibility
   */
  onAddSoundModalNewToggle: PropTypes.func,
  onAddAvatarModalToggle: PropTypes.func,
  /**
   * Whether new Add Sound modal is open
   */
  showAddSoundModalNew: PropTypes.bool,
  /**
   * Training ID for share settings
   */
  trainingId: PropTypes.string,
  /**
   * Toggle Infographic Scene modal visibility
   */
  onInfographicSceneModalToggle: PropTypes.func,
  /**
   * Whether Infographic Scene modal is open
   */
  showInfographicSceneModal: PropTypes.bool,
  /**
   * Toggle Question Generator modal visibility
   */
  onQuestionGeneratorModalToggle: PropTypes.func,
  /**
   * Whether Question Generator modal is open
   */
  showQuestionGeneratorModal: PropTypes.bool,
};

/**
 * TODO: Si queremos parametrizar esta clase hacemos un json y lo recorremos con los siguientes elementos:
 *
 *  {
 *    navbar:[{
 *      title,
 *      className,
 *      disabled,
 *      tooltip,
 *      icon,
 *      onClick [func],
 *      shownCondition [func],
 *    },{},...],
 *    dropdownMenu: [{title,
 *      className,
 *      disabled,
 *      tooltip,
 *      icon,
 *      onClick [func],
 *      shownCondition [func],}]
 *  }
 * */

export default EditorNavBar;
